import {Modal, StyleSheet, Text, TextInput} from "react-native";
import {EditableEvent, EditEventRequest} from "@/lib/types";
import {useLocalSearchParams, useRouter} from "expo-router";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import Page from "@/lib/components/Page";
import Card from "@/lib/components/Card";
import Location from "@/lib/components/Location";
import ErrorPage from "@/lib/components/ErrorPage";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import SpinnerPage from "@/lib/components/SpinnerPage";
import {Color} from "@/lib/styles";
import Nav from "@/lib/components/Nav";
import ActionButton from "@/lib/components/ActionButton";
import {useEffect, useMemo, useState} from "react";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Editable from "@/lib/components/Editable";
import Center from "@/lib/components/Center";
import {Calendar} from "@/lib/components/Calendar";
import {dateIntoParts, parseDateTime} from "@/lib/time";
import Timestamp from "@/lib/components/Timestamp";
import Colorpicker from "@/lib/components/Colorpicker";
import {colorName} from "@/lib/color";
import Header from "@/lib/components/Header";
import Radio from "@/lib/components/Radio";
import globstyles from "@/lib/globstyles";
import PrimaryText from "@/lib/components/PrimaryText";
import {useEvent} from "@/lib/components/EventProvider";

type EditableComponent =
    null
    | "color"
    | "title"
    | "location"
    | "start"
    | "end"
    | "info"
    | "visibility"
    | "permissions";

export default function EventPage() {
    // Start editing this component
    const [isEditing, setIsEditing] = useState<EditableComponent>(null);
    const [isConfirmDelete, setIsConfirmDelete] = useState(false);

    const {event_id} = useLocalSearchParams();
    const {session} = useSession();
    const router = useRouter();
    const queryClient = useQueryClient();
    const [busy, setBusy] = useState(false);
    const [saveError, setSaveError] = useState<Error | null>(null);

    const [originalDraft, setOriginalDraft] = useState<EditEventRequest | null>(null);
    const [draft, setDraft] = useState<EditEventRequest | null>(null);
    const [notify, setNotify] = useState(false);

    const {event, refreshEvent} = useEvent();

    const {data, isPending, error} = useQuery<EditableEvent>({
        queryKey: ['event', event_id, 'edit', session],
        queryFn: () => fetchBackend(`/event/${event_id}/edit`, "GET", session),
        retry: dataRetryPolicy,
    })

    useEffect(() => {
        if (data) {
            setOriginalDraft(data.event);
            setDraft(data.event);
        }
    }, [data])

    const colorNameMemo = useMemo(() => {
        return colorName(data?.event.color!);
    }, [data?.event.color])

    const infoWordCount = useMemo(() => {
            return data?.event.info?.split(/\s+/).length || 0;
        }, [data?.event.info]
    )

    const visibilityMemo = useMemo(() => {
        switch (data?.event.access_level) {
            case "public":
                return "Åpent for alle"
            case "private":
                return "Kun inviterte"
            case "secret":
                return "Deling med hemmelig lenke"
        }
    }, [data?.event.access_level]);

    const permissionsMemo = useMemo(() => {
        const flags: string[] = [];
        if (data?.event.open_invite) {
            flags.push("Alle kan invitere");
        }
        if (data?.event.open_posting) {
            flags.push("Alle kan starte diskusjon");
        }
        if (flags.length === 0) {
            return "Ingen";
        }
        return flags.join("\n");
    }, [data]);

    if (error) {
        return <ErrorPage error={error}/>
    } else if (isPending || !data || !draft) {
        return <SpinnerPage/>
    }

    const saveAndPublish = async () => {
        setBusy(true);
        const merged: EditEventRequest = {...draft, ...{publish: true, notify}};
        fetchBackendNoContent(`/event/${data.event.event_id}`, "PATCH", session, merged).then(async () => {
            await queryClient.invalidateQueries({queryKey: ['event', event_id, 'edit', session]});
            refreshEvent();
            router.dismiss();
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    const cancelOrDelete = async () => {
        setBusy(true);
        fetchBackendNoContent(`/event/${data.event.event_id}`, "DELETE", session).then(async () => {
            refreshEvent();
            router.dismissTo(`/e`);
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    const saveDraft = async (changes?: EditEventRequest) => {
        setSaveError(null);
        setBusy(true);
        const merged = {...draft, ...changes};
        setDraft(merged);
        fetchBackendNoContent(`/event/${data.event.event_id}`, "PATCH", session, merged).then(async () => {
            await queryClient.invalidateQueries({queryKey: ['event', event_id, 'edit', session]});
            setIsEditing(null);
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setIsEditing(null);
            setBusy(false);
        });
    }

    function storeInDraft(updates: EditEventRequest) {
        const merged: EditEventRequest = {...draft, ...updates};
        setDraft(merged);
    }

    function startEdit(component: EditableComponent) {
        setSaveError(null);
        setIsEditing(component);
    }

    async function cancelEdit() {
        setDraft(originalDraft);
        setIsEditing(null);
    }

    const hasParticipants: boolean = event && event.participants.length > 1 || false;

    return (
        <Page scrollable={true}>
            <Card
                image_url={data.event.image_url}
                color={data.event.color}
                contrast_color={data.event.contrast_color}
                onEditTitle={() => startEdit("title")}
                onEditImage={() => router.push(`/e/${data.event.event_id}/banner`)}
                title={draft.title!}
            >
                <Editable color={data.event.color} onPress={() => startEdit("color")}>
                    <Text style={styles.editPart}>Farge</Text>
                    <Text style={styles.editValue}>{colorNameMemo}</Text>
                </Editable>
                <Editable color={data.event.color} onPress={() => startEdit("start")}>
                    <Text style={styles.editPart}>Start</Text>
                    <Timestamp start={new Date(data.event.start)}/>
                </Editable>
                <Editable color={data.event.color} onPress={() => startEdit("end")}>
                    <Text style={styles.editPart}>Slutt</Text>
                    {data.event.end && <Timestamp start={new Date(data.event.end)}/>}
                    {!data.event.end && <Text style={styles.editValue}>–</Text>}
                </Editable>
                <Editable color={data.event.color} onPress={() => startEdit("location")}>
                    <Text style={styles.editPart}>Sted</Text>
                    {data.event.location && <Location>{data.event.location}</Location>}
                    {!data.event.location && <Text style={styles.editValue}>–</Text>}
                </Editable>
                <Editable color={data.event.color} onPress={() => startEdit("visibility")}>
                    <Text style={styles.editPart}>Synlighet</Text>
                    <Text style={styles.editValue}>{visibilityMemo}</Text>
                </Editable>
                <Editable color={data.event.color} onPress={() => startEdit("permissions")}>
                    <Text style={styles.editPart}>Tilganger</Text>
                    <Text style={styles.editValue}>{permissionsMemo}</Text>
                </Editable>
                <Editable color={data.event.color} onPress={() => startEdit("info")}>
                    <Text style={styles.editPart}>Info</Text>
                    {infoWordCount > 0 && <Text style={styles.editValue}>{infoWordCount} ord</Text>}
                    {infoWordCount === 0 && <Text style={styles.editValue}>–</Text>}
                </Editable>
            </Card>

            {hasParticipants &&
                <Radio title={"Send push-varsler"}
                       description={"Kryss av for å sende ut push-varsler til alle påmeldte og følgere av eventet."}
                       selected={notify}
                       checkbox={true}
                       onPress={() => setNotify(!notify)}
                />
            }

            {saveError && <ErrorMessage error={saveError}/>}

            <Nav>
                <ActionButton
                    icon={"checkmark"}
                    title={"Lagre og publiser"}
                    onPress={saveAndPublish}
                    busy={busy}
                    color={Color.Green}
                />
                {data.event.permissions.delete && !!event &&
                    <ActionButton
                        icon={hasParticipants ? "cancel" : "delete"}
                        title={hasParticipants ? "Avlys arrangementet" : "Slett arrangementet"}
                        onPress={() => setIsConfirmDelete(true)}
                        busy={busy}
                    />}
            </Nav>

            <Modal onRequestClose={() => setIsConfirmDelete(false)}
                   visible={isConfirmDelete}
                   allowSwipeDismissal={true}
                   presentationStyle={"pageSheet"}
                   animationType={"slide"}
            >
                <Page>
                    <Center>
                        {hasParticipants && <>
                            <Header title={"Bekreft avlysing av arrangement"}/>
                            <PrimaryText>
                                Handlingen er permanent. Påmeldte, inviterte og interesserte blir varslet om avlysingen.
                            </PrimaryText>
                            <PrimaryText>
                                Påmelding stoppes, men eventets informasjon vil
                                fortsatt være tilgjengelig og mulig å redigere.
                            </PrimaryText>
                            <PrimaryText>
                                Hvis du vil gi ytterligere informasjon til dine gjester, kan du bruke eventets
                                informasjonsboks.
                            </PrimaryText>
                        </>}
                        {!hasParticipants && <>
                            <Header title={"Bekreft sletting av arrangement"}/>
                            <PrimaryText>
                                Eventet har ingen gjester og vil slettes permanent.
                            </PrimaryText>
                        </>}
                        <Nav>
                            <ActionButton
                                title={"Avbryt"}
                                onPress={() => setIsConfirmDelete(false)}
                            />
                            <ActionButton
                                title={hasParticipants ? "Avlys arrangementet" : "Slett arrangementet"}
                                color={Color.Red}
                                icon={"cancel"}
                                onPress={cancelOrDelete}
                            />
                        </Nav>
                    </Center>
                </Page>
            </Modal>

            <Modal onRequestClose={cancelEdit}
                   visible={!!isEditing}
                   allowSwipeDismissal={true}
                   presentationStyle={"pageSheet"}
                   animationType={"slide"}
            >
                <Page>
                    {isEditing === "color" &&
                        <Center>
                            <Header title={"Velg farge"} center={true}/>
                            <Colorpicker color={data.event.color} onChange={async (color) => {
                                await saveDraft({color: color.color})
                            }}/>
                            <Nav>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                    {isEditing === "visibility" &&
                        <Center>
                            <Header title={"Synlighet"} center={true}/>
                            <Radio
                                title={"Åpent for alle"}
                                description={"Offentlig arrangement. Alle kan melde seg på. Synlig for søkemotorer og Internett."}
                                checkbox={false}
                                selected={draft.access_level === "public"}
                                onPress={() => {
                                    storeInDraft({access_level: "public"});
                                }}
                            />
                            <Radio
                                title={"Deling med hemmelig lenke"}
                                description={"Alle som har lenken til arrangementet kan se detaljene og melde seg på."}
                                checkbox={false}
                                selected={draft.access_level === "secret"}
                                onPress={() => {
                                    storeInDraft({access_level: "secret"});
                                }}
                            />
                            <Radio
                                title={"Kun inviterte"}
                                description={"Krev invitasjon for å se arrangementet og detaljene."}
                                checkbox={false}
                                selected={draft.access_level === "private"}
                                onPress={() => {
                                    storeInDraft({access_level: "private"});
                                }}
                            />
                            <Nav>
                                <ActionButton title={"Bruk"} icon={"checkmark"} onPress={() => saveDraft()} busy={busy}
                                              color={Color.Green}/>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                    {isEditing === "permissions" &&
                        <Center>
                            <Header title={"Tilganger"} center={true}/>
                            <Radio
                                title={"Alle kan invitere"}
                                description={"Alle som har tilgang til arrangementet kan invitere sine egne venner."}
                                checkbox={true}
                                selected={!!draft.open_invite}
                                onPress={() => {
                                    storeInDraft({open_invite: !draft?.open_invite});
                                }}
                            />
                            <Radio
                                title={"Alle kan starte diskusjon"}
                                description={"Påmeldte og interesserte kan starte nye diskusjonstråder."}
                                checkbox={true}
                                selected={!!draft.open_posting}
                                onPress={() => {
                                    storeInDraft({open_posting: !draft?.open_posting});
                                }}
                            />
                            <Nav>
                                <ActionButton title={"Bruk"} icon={"checkmark"} onPress={() => saveDraft()} busy={busy}
                                              color={Color.Green}/>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                    {isEditing === "title" &&
                        <Center>
                            <Header title={"Tittel for eventet"} center={true}/>
                            <TextInput
                                style={[
                                    styles.textInput,
                                    globstyles.cardTitle,
                                    {
                                        color: data.event.contrast_color,
                                        backgroundColor: data.event.color,
                                        borderWidth: 0,
                                    },
                                ]}
                                value={draft.title || ""}
                                onSubmitEditing={async () => {
                                    await saveDraft();
                                }}
                                onChangeText={(text) => {
                                    storeInDraft({title: text});
                                }}
                                autoFocus={true}
                            />
                            <Nav>
                                <ActionButton title={"Bruk"} icon={"checkmark"} onPress={() => saveDraft()} busy={busy}
                                              color={Color.Green}/>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                    {isEditing === "location" &&
                        <Center>
                            <Header title={"Hvor skjer det?"} center={true}/>
                            <TextInput
                                style={[
                                    styles.textInput,
                                    {borderColor: data.event.color},
                                ]}
                                value={draft.location || ""}
                                onSubmitEditing={async () => {
                                    await saveDraft();
                                }}
                                onChangeText={(text) => {
                                    storeInDraft({location: text});
                                }}
                                autoFocus={true}
                            />
                            <Nav>
                                <ActionButton title={"Bruk"} icon={"checkmark"} onPress={() => saveDraft()} busy={busy}
                                              color={Color.Green}/>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                    {isEditing === "info" &&
                        <Center style={{flex: 1, paddingVertical: 64}}>
                            <Header title={"Lang beskrivelse"} center={true}/>
                            <TextInput
                                style={[
                                    styles.textArea,
                                    {borderColor: data.event.color},
                                ]}
                                value={draft.info || ""}
                                multiline={true}
                                textAlignVertical={"top"}
                                onSubmitEditing={async () => {
                                    await saveDraft();
                                }}
                                onChangeText={(text) => {
                                    storeInDraft({info: text});
                                }}
                            />
                            <Nav>
                                <ActionButton title={"Bruk"} icon={"checkmark"} onPress={() => saveDraft()} busy={busy}
                                              color={Color.Green}/>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                    {isEditing === "start" &&
                        <Center>
                            <Header title={"Velg starttid"} center={true}/>
                            <Calendar initialDate={data.event.start}
                                      onChange={(date, time) => {
                                          const parts = data.event.end ? dateIntoParts(parseDateTime(data.event.end)) : null;
                                          storeInDraft({
                                              start_date: date,
                                              start_time: time,
                                              end_date: parts ? parts.date : null,
                                              end_time: parts ? parts.time : null,
                                          })
                                      }}
                                      onSubmit={() => saveDraft()}
                            />
                            <Nav>
                                <ActionButton title={"Bruk"} icon={"checkmark"} onPress={() => saveDraft()} busy={busy}
                                              color={Color.Green}/>
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                    {isEditing === "end" &&
                        <Center>
                            <Header title={"Velg sluttid"} center={true}/>
                            <Calendar initialDate={data.event.end || data.event.start}
                                      onChange={(date, time) => {
                                          const parts = dateIntoParts(parseDateTime(data.event.start));
                                          storeInDraft({
                                              start_date: parts.date,
                                              start_time: parts.time,
                                              end_date: date,
                                              end_time: time,
                                          })
                                      }}
                                      onSubmit={() => saveDraft()}
                            />
                            <Nav>
                                <ActionButton title={"Har ikke sluttid"} onPress={async () => {
                                    const parts = dateIntoParts(parseDateTime(data.event.start));
                                    await saveDraft({
                                        start_date: parts.date,
                                        start_time: parts.time,
                                        end_date: null,
                                        end_time: null,
                                    });
                                }}/>
                            </Nav>
                            <Nav>
                                <ActionButton
                                    title={"Bruk"}
                                    icon={"checkmark"}
                                    onPress={() => saveDraft()}
                                    busy={busy}
                                    color={Color.Green}
                                />
                                <ActionButton title={"Avbryt"} onPress={() => setIsEditing(null)}/>
                            </Nav>
                        </Center>
                    }
                </Page>
            </Modal>
        </Page>
    )
}

export const styles = StyleSheet.create({
    text: {
        color: Color.PrimaryText,
        fontSize: 16,
        lineHeight: 26,
        width: "100%",
    },
    editPart: {
        color: Color.PrimaryText,
        fontSize: 16,
        fontWeight: 600,
    },
    editValue: {
        color: Color.PrimaryText,
        fontSize: 16,
    },
    textInput: {
        width: "100%",
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: Color.Yellow,
        borderRadius: 7,
        padding: 12,
        fontSize: 16,
        color: Color.PrimaryText,
        marginBottom: 8,
    },
    textArea: {
        width: "100%",
        flex: 1,
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: Color.Yellow,
        borderRadius: 7,
        textAlignVertical: "top",
        padding: 8,
        fontSize: 16,
        color: Color.PrimaryText,
        marginBottom: 8,
    },
    datePicker: {
        color: Color.PrimaryText,
    }
})